import { useCallback, useRef, type FC } from 'react';
import {
  Timer,
  DEFAULT_TIMER_DURATION,
  DEFAULT_TIMER_MODE,
  durationToMs,
  TimerProgressRing,
  type TimerProgressRingHandle,
} from '@/components/Timer';
import type { TimerBlock } from '@/components/canvas/blocks/types';
import { BlockHostView } from '@/components/canvas/blocks/BlockHostView';
import type { BlockViewProps } from '@/components/canvas/blocks/registry/types';
import { estimateTimerContentSize } from '@/components/canvas/blocks/timer/contentMetrics';
import { useCanvasStore } from '@/components/canvas/store/canvas.store';

export const TimerBlockView: FC<BlockViewProps<TimerBlock>> = ({
  block,
  displayLayout,
  onPointerDown,
  isPresentationMode,
}) => {
  const { data } = block;
  const contentSize = estimateTimerContentSize(data);
  const timerSize = data.size ?? contentSize.width;
  const target = data.defaultTarget ?? DEFAULT_TIMER_DURATION;
  const mode = data.mode ?? DEFAULT_TIMER_MODE;
  const showProgressRing = durationToMs(target) > 0;
  const ringRef = useRef<TimerProgressRingHandle>(null);
  const updateBlockData = useCanvasStore((s) => s.updateBlockData);

  const handleTargetChange = useCallback(
    (next: typeof target) => {
      updateBlockData(block.id, { defaultTarget: next });
    },
    [block.id, updateBlockData],
  );

  const handleModeChange = useCallback(
    (next: typeof mode) => {
      updateBlockData(block.id, { mode: next });
    },
    [block.id, updateBlockData],
  );

  const handleProgressTick = useCallback((progress: number) => {
    ringRef.current?.setValue(progress);
  }, []);

  return (
    <BlockHostView
      block={block}
      displayLayout={displayLayout}
      contentSize={contentSize}
      contentOverlay={
        showProgressRing ? (
          <TimerProgressRing ref={ringRef} value={0} className="size-full" />
        ) : undefined
      }
      onPointerDown={
        onPointerDown ? (e) => onPointerDown(e, block) : undefined
      }
      isPresentationMode={isPresentationMode}
    >
      <Timer
        key={block.id}
        sessionId={block.id}
        size={timerSize}
        defaultTarget={target}
        defaultMode={mode}
        hideRing
        onProgressTick={handleProgressTick}
        onTargetChange={handleTargetChange}
        onModeChange={handleModeChange}
      />
    </BlockHostView>
  );
};
